import { Component, OnDestroy, OnInit } from '@angular/core';
import { Chart } from 'chart.js/auto';
import { interval, Subscription } from 'rxjs';
import { DataService } from 'src/app/data.service';
import { CorTipoTicket, DescricaoTipoTicket, TipoTicketEnum } from 'src/app/enum/tipoTicket.enum';
import { DashboardResumo, DashboardService, Distribuicao } from 'src/app/service/dashboard.service';
import { SegmentoService } from 'src/app/service/segmento.service';
import { Segmento } from '../segmento/segmento';

interface PeriodoPreset {
  label: string;
  dias: number | 'mes' | 'ano';
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {

  resumo: DashboardResumo | null = null;
  carregando = false;

  segmentos: Segmento[] = [];
  filtroSegmento: string | null = null;
  dataInicio = '';
  dataFim = '';

  permissoes: string[] = [];
  private permSub?: Subscription;

  readonly intervaloSegundos = 30;
  autoRefresh = true;
  segundosRestantes = this.intervaloSegundos;
  ultimaAtualizacao: Date | null = null;
  private refreshSub?: Subscription;

  readonly presets: PeriodoPreset[] = [
    { label: '7 dias', dias: 7 },
    { label: '30 dias', dias: 30 },
    { label: 'Mês atual', dias: 'mes' },
    { label: 'Ano', dias: 'ano' },
  ];
  presetAtivo = 30 as number | 'mes' | 'ano';

  private charts: Chart[] = [];

  private readonly statusLabels: { [k: string]: string } = {
    EM_ANALISE: 'Em análise',
    EM_DESENVOLVIMENTO: 'Em desenvolvimento',
    EM_HOMOLOGACAO: 'Em homologação',
    AGUARNDANDO_CLIENTE: 'Aguardando cliente',
    AGUARDANDO_ATUALIZACAO_SISTEMA: 'Aguardando atualização',
    AGUARDANDO_HOMOLOGACAO_CLIENTE: 'Aguardando homolog. cliente',
    AGUARNDANDO_MERGE: 'Aguardando merge',
    RESOLVIDO: 'Resolvido',
    FINALIZADO_CLIENTE: 'Finalizado (cliente)',
    FINALIZADO_AUTOMATICO: 'Finalizado (auto)',
    BACKLOG: 'Backlog',
  };

  private readonly prioridadeLabels: { [k: string]: string } = {
    BAIXA: 'Baixa',
    MEDIA: 'Média',
    ALTA: 'Alta',
    URGENTE: 'Urgente',
    CRITICA: 'Crítica',
  };

  constructor(
    private dashboardService: DashboardService,
    private segmentoService: SegmentoService,
    private dataService: DataService,
  ) { }

  ngOnInit(): void {
    this.permSub = this.dataService.permissao$.subscribe(p => this.permissoes = p || []);
    this.dataService.carregarPermissoes();
    this.segmentoService.listarAtivos().subscribe(lista => this.segmentos = lista, () => { });
    this.aplicarPreset(30);
    this.iniciarAutoRefresh();
  }

  ngOnDestroy(): void {
    this.permSub?.unsubscribe();
    this.pararAutoRefresh();
    this.destruirCharts();
  }

  tem(permissao: string): boolean {
    return this.permissoes.includes(permissao);
  }

  private iniciarAutoRefresh(): void {
    this.pararAutoRefresh();
    this.segundosRestantes = this.intervaloSegundos;
    this.refreshSub = interval(1000).subscribe(() => {
      this.segundosRestantes--;
      if (this.segundosRestantes <= 0) {
        this.segundosRestantes = this.intervaloSegundos;
        this.carregar(true);
      }
    });
  }

  private pararAutoRefresh(): void {
    this.refreshSub?.unsubscribe();
    this.refreshSub = undefined;
  }

  get exibeFinanceiro(): boolean {
    return this.tem('CONTA_PAGAR') || this.tem('CONTA_RECEBER');
  }

  aplicarPreset(dias: number | 'mes' | 'ano') {
    this.presetAtivo = dias;
    const hoje = new Date();
    let inicio: Date;
    if (dias === 'mes') {
      inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    } else if (dias === 'ano') {
      inicio = new Date(hoje.getFullYear(), 0, 1);
    } else {
      inicio = new Date();
      inicio.setDate(hoje.getDate() - dias);
    }
    this.dataInicio = this.toIso(inicio);
    this.dataFim = this.toIso(hoje);
    this.carregar();
  }

  carregar(silencioso = false) {
    if (!silencioso) {
      this.carregando = true;
    }
    if (this.autoRefresh) {
      this.segundosRestantes = this.intervaloSegundos;
    }
    this.dashboardService.resumo(this.dataInicio, this.dataFim, this.filtroSegmento).subscribe(res => {
      this.resumo = res;
      this.carregando = false;
      this.ultimaAtualizacao = new Date();
      setTimeout(() => this.montarCharts(), 0);
    }, () => {
      this.carregando = false;
    });
  }

  onFiltroManual() {
    this.presetAtivo = -1 as any;
    this.carregar();
  }

  onSegmentoChange() {
    this.carregar();
  }

  formatarMoeda(valor: number | null | undefined): string {
    const v = valor ?? 0;
    return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  // ── Charts ────────────────────────────────────────────────────────────────
  private destruirCharts() {
    this.charts.forEach(c => c.destroy());
    this.charts = [];
  }

  private montarCharts() {
    this.destruirCharts();
    if (!this.resumo) {
      return;
    }
    if (this.tem('TICKET')) {
      this.montarDoughnut('chartTicketStatus', this.resumo.tickets.porStatus, l => this.statusLabels[l] || l,
        l => this.corStatus(l), 'Chamados');
      this.montarBarras('chartTicketPrioridade', this.resumo.tickets.porPrioridade,
        l => this.prioridadeLabels[l] || l, l => this.corPrioridade(l));
      this.montarBarras('chartTicketTipo', this.resumo.tickets.porTipo || [],
        l => this.labelTipo(l), l => this.corTipo(l));
      this.montarBarras('chartTicketResponsavel', this.resumo.tickets.porResponsavel,
        l => l, l => this.corPorRotulo(l), true);
      this.montarBarras('chartTicketSegmento', this.resumo.tickets.porSegmento,
        l => l, l => this.corPorRotulo(l));
      this.montarBarras('chartTicketCliente', this.resumo.tickets.porCliente,
        l => l, l => this.corPorRotulo(l), true);
    }
    if (this.exibeFinanceiro) {
      this.montarFluxo();
    }
  }

  private montarDoughnut(id: string, dados: Distribuicao[], labelFn: (l: string) => string,
    corFn: (l: string) => string, centro: string) {
    const ctx = document.getElementById(id) as HTMLCanvasElement | null;
    if (!ctx || !dados?.length) {
      return;
    }
    const labels = dados.map(d => labelFn(d.label));
    const valores = dados.map(d => d.quantidade);
    const cores = dados.map(d => corFn(d.label));
    const centerText = {
      id: 'centerText',
      beforeDraw: (chart: any) => {
        const { ctx: c, chartArea } = chart;
        if (!chartArea) { return; }
        const x = (chartArea.left + chartArea.right) / 2;
        const y = (chartArea.top + chartArea.bottom) / 2;
        const total = chart.data.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
        c.save();
        c.font = 'bold 22px Arial';
        c.fillStyle = '#333';
        c.textAlign = 'center';
        c.textBaseline = 'middle';
        c.fillText(total.toString(), x, y - 8);
        c.font = '13px Arial';
        c.fillStyle = '#888';
        c.fillText(centro, x, y + 14);
        c.restore();
      }
    };
    this.charts.push(new Chart(ctx, {
      type: 'doughnut',
      data: { labels, datasets: [{ data: valores, backgroundColor: cores, borderWidth: 2, spacing: 2 }] },
      plugins: [centerText],
      options: { cutout: '65%', plugins: { legend: { position: 'bottom' } } } as any
    }));
  }

  private montarBarras(id: string, dados: Distribuicao[], labelFn: (l: string) => string,
    cor: string | ((label: string) => string), horizontal = false) {
    const ctx = document.getElementById(id) as HTMLCanvasElement | null;
    if (!ctx || !dados?.length) {
      return;
    }
    const cores = typeof cor === 'function'
      ? dados.map(d => cor(d.label))
      : dados.map(() => cor);
    this.charts.push(new Chart(ctx, {
      type: 'bar',
      data: {
        labels: dados.map(d => labelFn(d.label)),
        datasets: [{
          label: 'Quantidade',
          data: dados.map(d => d.quantidade),
          backgroundColor: cores,
          borderColor: cores,
          borderWidth: 1,
          borderRadius: 6,
        }]
      },
      options: {
        indexAxis: horizontal ? 'y' : 'x',
        plugins: { legend: { display: false } },
        scales: { x: { beginAtZero: true }, y: { beginAtZero: true } }
      } as any
    }));
  }

  private montarFluxo() {
    const ctx = document.getElementById('chartFluxo') as HTMLCanvasElement | null;
    if (!ctx || !this.resumo!.fluxoMensal?.length) {
      return;
    }
    const serie = this.resumo!.fluxoMensal;
    this.charts.push(new Chart(ctx, {
      type: 'bar',
      data: {
        labels: serie.map(s => s.mes),
        datasets: [
          { label: 'A Receber', data: serie.map(s => s.aReceber), backgroundColor: '#1E88E5', borderRadius: 6 },
          { label: 'A Pagar', data: serie.map(s => s.aPagar), backgroundColor: '#E53935', borderRadius: 6 },
        ]
      },
      options: {
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            callbacks: {
              label: (c: any) => `${c.dataset.label}: ${this.formatarMoeda(c.raw)}`
            }
          }
        },
        scales: { y: { beginAtZero: true } }
      } as any
    }));
  }

  private readonly paletteCores = [
    '#1E88E5', '#E53935', '#43A047', '#FB8C00', '#8E24AA',
    '#00897B', '#F4511E', '#5E35B1', '#039BE5', '#C0CA33',
    '#D81B60', '#6D4C41', '#3949AB', '#00ACC1', '#7CB342',
  ];

  /** Cor estável por rótulo (mesmo cliente/responsável sempre na mesma cor). */
  private corPorRotulo(label: string): string {
    const texto = (label || '').trim().toUpperCase();
    let hash = 0;
    for (let i = 0; i < texto.length; i++) {
      hash = ((hash << 5) - hash) + texto.charCodeAt(i);
      hash |= 0;
    }
    return this.paletteCores[Math.abs(hash) % this.paletteCores.length];
  }

  private corPrioridade(prioridade: string): string {
    const cores: Record<string, string> = {
      BASICA: '#546E7A',
      MEDIA: '#FB8C00',
      ALTA: '#E53935',
    };
    return cores[prioridade] || this.corPorRotulo(prioridade);
  }

  private labelTipo(tipo: string): string {
    return DescricaoTipoTicket.get(tipo as TipoTicketEnum) || tipo;
  }

  private corTipo(tipo: string): string {
    return CorTipoTicket.get(tipo as TipoTicketEnum) || this.corPorRotulo(tipo);
  }

  private corStatus(status: string): string {
    const cores: any = {
      EM_ANALISE: '#2196F3',
      EM_DESENVOLVIMENTO: '#9C27B0',
      EM_HOMOLOGACAO: '#7E57C2',
      AGUARNDANDO_CLIENTE: '#FF9800',
      AGUARDANDO_ATUALIZACAO_SISTEMA: '#FFC107',
      AGUARDANDO_HOMOLOGACAO_CLIENTE: '#FFB300',
      AGUARNDANDO_MERGE: '#00BCD4',
      RESOLVIDO: '#4CAF50',
      FINALIZADO_CLIENTE: '#2E7D32',
      FINALIZADO_AUTOMATICO: '#66BB6A',
      BACKLOG: '#9E9E9E'
    };
    return cores[status] || this.corPorRotulo(status);
  }

  private toIso(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
